/** One observer per landing. Content stays readable when motion is unavailable. */
export class LandingMotion {
  private observer?: IntersectionObserver;
  private readonly preference = window.matchMedia?.('(prefers-reduced-motion: reduce)');

  constructor(private readonly root: HTMLElement) {
    if (this.preference?.matches || typeof IntersectionObserver === 'undefined') return;

    this.observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.reveal(entry.target);
      }
    }, { threshold: 0.12 });

    this.root.classList.add('motion-enabled');
    this.root.querySelectorAll('[data-reveal]').forEach(element => this.observer?.observe(element));
    this.root.addEventListener('focusin', this.onFocus);
    this.preference?.addEventListener('change', this.onPreferenceChange);
  }

  private reveal(element: Element): void {
    element.classList.add('is-visible');
    this.observer?.unobserve(element);
  }

  // Keyboard navigation must never land in visually hidden content.
  private readonly onFocus = (event: FocusEvent): void => {
    if (!(event.target instanceof Element)) return;
    let element: Element | null = event.target;
    while (element && element !== this.root) {
      if (element.hasAttribute('data-reveal')) {
        element.classList.add('motion-immediate');
        this.reveal(element);
      }
      element = element.parentElement;
    }
  };

  private readonly onPreferenceChange = (): void => {
    // Finish immediately, and do not replay content if the preference changes back.
    if (this.preference?.matches) this.destroy();
  };

  destroy(): void {
    this.observer?.disconnect();
    this.root.classList.remove('motion-enabled');
    this.root.removeEventListener('focusin', this.onFocus);
    this.preference?.removeEventListener('change', this.onPreferenceChange);
  }
}
